import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ErrorBoundary, FDProvider, ToastProvider } from "@fd/ui";
// Self-hosted brand fonts (Inter for UI, JetBrains Mono for IDs/amounts) — family
// names "Inter" / "JetBrains Mono" match tokens.css exactly.
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/600.css";
import "@fd/tokens/css";
import "@fd/ui/css";
import { App } from "./App";
import { setCatalog, type Catalog, setCatalogEntitlements, type CatalogEntitlements } from "@fd/pricing";

// Hydrate the pricing engine from the live published catalog (/api/public/catalog) before
// first render, so Products + the offer builder reflect the latest published prices with NO
// redeploy. Bounded by a short timeout; on a slow/failed fetch we keep the bundled snapshot.
async function hydrateCatalog(): Promise<void> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2500);
    const res = await fetch("/api/public/catalog", { headers: { Accept: "application/json" }, signal: ctrl.signal });
    clearTimeout(timer);
    if (!res.ok) return;
    const data = (await res.json()) as { catalog?: Catalog; entitlements?: CatalogEntitlements | null };
    if (data.catalog) setCatalog(data.catalog);
    // Entitlements ride beside the snapshot (not inside it). No bundled fallback —
    // panels stay hidden until this hydrates.
    if (data.entitlements) setCatalogEntitlements(data.entitlements);
  } catch {
    /* keep the bundled fallback */
  }
}

void hydrateCatalog().finally(() => {
  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <FDProvider theme="light">
        <ToastProvider>
          <ErrorBoundary>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </ErrorBoundary>
        </ToastProvider>
      </FDProvider>
    </React.StrictMode>
  );
});
